import { Fragment, useEffect, useMemo, useRef, useState } from "react";
import { useProjectStore } from "@/zapi-frontend/stores/project";
import { LoadingSpinner, FigmaTypography } from "@libs/zapi-ui/custom";
import { tryCatch } from "@/zapi-frontend/shared/lib/tryCatch";
import { toast } from "sonner";
import { InvisibleTextarea } from "@/zapi-frontend/shared/ui/custom/InvisibleTextarea";
import { useMeStore } from "@/zapi-frontend/shared/hooks/useMe";
import { hasEditAccess } from "@/zapi-frontend/shared/utils/permissions";

function Divider() {
  return <div className="w-full h-[1px] border-t border-innoveraZinc-200 my-5" />;
}

export function InstructionsIsland() {
  const [projectData, setProjectData] = useState<null | Record<string, string>>(null);
  const [projectInstructions, setProjectInstructions] = useState("");
  const { updateProject, selectedProject, isFetchProjectLoading } = useProjectStore();
  const { isLabs, role, accessMode } = useMeStore();
  const hasEdit = hasEditAccess(role, accessMode);

  const saveTimeotRef = useRef<NodeJS.Timeout | null>(null);
  const abortControllerRef = useRef<AbortController | null>(null);

  // This is for avoiding ui flickering during project change
  const lastProjectNamesMapRef = useRef<Record<string, string> | undefined>(undefined);
  const projectNamesMap: Record<string, string> | undefined = useMemo(() => {
    if (selectedProject?.projectChapters?.length) {
      const map = selectedProject.projectChapters
        .filter((chapter) => {
          if (chapter.tag === "iq_generated_appendix") return false;
          if (!isLabs && chapter.isHidden) return false;
          return true;
        })
        .reduce((acc, chapter) => {
          acc[chapter.projectTemplateChapter.slug] = chapter.projectTemplateChapter.name;
          return acc;
        }, {} as Record<string, string>);
      lastProjectNamesMapRef.current = map;
      return map;
    }
    return lastProjectNamesMapRef.current;
  }, [selectedProject?.projectChapters, isLabs]);

  async function onSave(
    projectInstructions: string,
    projectData: Record<string, string>,
    abortController?: AbortController
  ) {
    if (!selectedProject?.id || !projectData) return;

    const [, error] = await tryCatch(
      updateProject(
        selectedProject.id.toString(),
        {
          chaptersInstructions: projectData,
          projectInstructions: projectInstructions
        },
        abortController
      )
    );

    if (error) {
      if (error.message !== "canceled") toast.error(error.message);
      return;
    }

    toast.success("Instructions updated successfully");
  }

  function debouncedOnSave(projectInstructions: string, projectData: Record<string, string>) {
    if (saveTimeotRef.current) {
      clearTimeout(saveTimeotRef.current);
    }
    if (abortControllerRef.current) {
      abortControllerRef.current.abort();
    }
    abortControllerRef.current = new AbortController();
    saveTimeotRef.current = setTimeout(() => {
      onSave(projectInstructions, projectData, abortControllerRef.current ?? undefined);
    }, 700);
  }

  useEffect(() => {
    if (selectedProject?.projectChapters) {
      setProjectData(
        selectedProject.projectChapters
          .filter((chapter) => {
            if (chapter.tag === "iq_generated_appendix") return false;
            if (!isLabs && chapter.isHidden) return false;
            return true;
          })
          .reduce((acc, chapter) => {
            acc[chapter.projectTemplateChapter.slug] = chapter.chapterInstructions ?? "";
            return acc;
          }, {} as Record<string, string>)
      );
      setProjectInstructions(selectedProject.projectInstructions ?? "");
    }
  }, [selectedProject?.projectInstructions, selectedProject?.projectChapters, isFetchProjectLoading, isLabs]);

  if ((!projectData && !projectNamesMap) || isFetchProjectLoading)
    return (
      <div className="w-full h-full flex items-center justify-center text-[14px] text-gray-500">
        <LoadingSpinner />
        Loading...
      </div>
    );

  return (
    <div className="flex w-full h-full">
      <div className="w-full flex flex-col bg-card h-[calc(100vh-120px)] pl-10 pr-14 overflow-auto pb-4 pt-4">
        <div>
          <div className="flex align-middle justify-between">
            <FigmaTypography variant="heading-1" className="flex items-center h-full justify-center" color="zinc900">
              Project Instructions
            </FigmaTypography>
          </div>

          <InvisibleTextarea
            value={projectInstructions}
            fullWidth
            isOptional
            placeholder="Enter project instructions"
            readOnly={!hasEdit}
            onChange={(e) => {
              const { value } = e.target;
              setProjectInstructions(value);
              debouncedOnSave(value, projectData ?? {});
            }}
            className="min-h-[120px]"
          />
        </div>
        <Divider />
        <FigmaTypography variant="heading-1" className="mb-5">
          Chapter Instructions
        </FigmaTypography>
        {Object.entries(projectData ?? {}).map(([key, value], index, array) => (
          <Fragment key={key}>
            <div>
              <FigmaTypography variant="heading-2">{projectNamesMap?.[key]}</FigmaTypography>
              <InvisibleTextarea
                key={index}
                value={value}
                fullWidth
                isOptional
                placeholder="Enter chapter instructions"
                readOnly={!hasEdit}
                onChange={(e) => {
                  const { value } = e.target;
                  setProjectData((prev) => {
                    const newData = { ...prev, [key]: value };
                    debouncedOnSave(projectInstructions, newData);
                    return newData;
                  });
                }}
                className="min-h-[120px]"
              />
            </div>
            {index !== array.length - 1 && <Divider />}
          </Fragment>
        ))}
      </div>
    </div>
  );
}
